import React, { FC, useState } from 'react';
import { Badge, Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { OrderItemProduct } from '..';
import * as OrderSummary from '../../../shared/orders/model';
import {
  MaterialIcon,
  OrderItemElement,
  OrderItemSelectOption,
  RefundRequestModal,
  UserStateComponent,
} from '../../components';
import { Currency } from '../../components/Currency';
import { PageHeader } from '../../components/PageHeader';
import { useTranslation } from '../../translations';
import { Fetcher, useFetcher, useUserSafe } from '../../utils';

export const OrderHistoryComponent: FC = () => {
  const { ts } = useTranslation();
  const user = useUserSafe();
  const [refundOrder, setRefundOrder] = useState<OrderSummary.Order | undefined>(undefined);
  const [modalOption, setModalOption] = useState<OrderItemSelectOption | undefined>(undefined);

  const fetcher = useFetcher(async () => {
    const [categories, orders] = await Promise.all([
      api.getProductCategories(),
      api.getOrdersByUserId(user.id).then((order) => order.sort((a, b) => b.id - a.id)),
    ]);

    return { categories, orders };
  }, [user]);

  if (!fetcher.success) {
    return (
      <UserStateComponent>
        <Fetcher result={fetcher} />
      </UserStateComponent>
    );
  }

  return (
    <UserStateComponent>
      <PageHeader>{ts('common.order_history')}</PageHeader>
      <Row className="justify-content-center" id="orderHistory">
        {fetcher.data.orders.length === 0 ? (
          <Col className="text-center" id="noOrderHistory" lg={8} xs={12}>
            <MaterialIcon large name="sentiment_dissatisfied" />
            <h4>{ts('order_history.youve_got_no_previous_orders')}</h4>
            <p>{ts('order_history.once_you_pay_for_an')}</p>
          </Col>
        ) : (
          <>
            {fetcher.data.orders
              .filter(({ orderItems }) => orderItems.length > 0)
              .map((order) => (
                <Col key={order.id} lg={8} xs={12}>
                  <OrderItemElement
                    categories={fetcher.data.categories}
                    hasRefunds={order.refunds.length > 0}
                    id={`order_${order.id}`}
                    isCustomer
                    onSelect={(opt) => {
                      setModalOption(opt);
                      setRefundOrder(order);
                    }}
                    onUpdate={fetcher.refresh}
                    order={order}
                  />
                </Col>
              ))}
            {refundOrder && modalOption === 'requestRefund' && (
              <RefundRequestModal
                onCancel={() => {
                  setModalOption(undefined);
                  setRefundOrder(undefined);
                }}
                onSuccess={() => {
                  setModalOption(undefined);
                  setRefundOrder(undefined);
                  fetcher.refresh();
                }}
                order={refundOrder}
              />
            )}
            {refundOrder && modalOption === 'viewRefundHistory' && (
              <OrderHistoryModal
                order={refundOrder}
                setModalOption={setModalOption}
                setOrder={setRefundOrder}
              />
            )}
          </>
        )}
      </Row>
    </UserStateComponent>
  );
};

const OrderHistoryModal: FC<{
  readonly order?: OrderSummary.Order;
  readonly setOrder: (state: OrderSummary.Order | undefined) => void;
  readonly setModalOption: (state: OrderItemSelectOption | undefined) => void;
}> = ({ order, setOrder, setModalOption }) => {
  const { t, ts } = useTranslation();
  return (
    <Modal className="modal-large" id="refundModal" isOpen>
      <ModalHeader>{t('order_history.refund_history_order', order?.id)}</ModalHeader>
      <ModalBody className="filter-table">
        {order!.refunds.map((refund) => (
          <div className="table-item" key={refund.id}>
            <h4>{ts('common.refund', refund.id)}</h4>
            {refund.status === 'pending' && (
              <Badge color="warning">{ts('common.pending_approval')}</Badge>
            )}
            {refund.status === 'denied' && <Badge color="danger">{ts('common.denied')}</Badge>}
            {refund.status === 'approved' && (
              <>
                <Badge color="success">{ts('common.approved')}</Badge>
                <small>
                  &mdash; <Currency value={refund.amount} /> {ts('order_list_item.refunded')}
                </small>
              </>
            )}
            <br />
            <br />
            <hr />
            {refund.items.map(({ itemId }) => {
              const item = order!.orderItems.find(({ id }) => id === itemId)!;
              return <OrderItemProduct item={item} key={item.id} order={order!} readOnly />;
            })}
          </div>
        ))}
      </ModalBody>
      <ModalFooter>
        <Button
          color="secondary"
          onClick={() => {
            setModalOption(undefined);
            setOrder(undefined);
          }}
        >
          {ts('common.close')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
